import { FleetAreaCode, FleetTaskGroup, PermissionCode } from '@gb-ticket/shared-types';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import {
  FLEET_DAMAGE_KIND_LABELS,
  FLEET_DAMAGE_STATUS_LABELS,
  formatDeDate,
  formatFleetDueDate,
  isFleetDateOverdue,
} from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import { FleetChargeLevel } from '@/products/fleet/features/fleet-charge-level';
import { FleetVehicleChargeLine } from '@/products/fleet/features/fleet-charge-status';
import { FleetSolarCard } from '@/products/fleet/features/fleet-solar-card';
import { FleetStatusBadge } from '@/products/fleet/features/fleet-status-badge';
import { fleetAreaOn, useFleetAreas } from '@/products/fleet/features/use-fleet-areas';
import { FleetManualTaskActions } from '@/products/fleet/features/fleet-manual-task-actions';
import {
  useFleetDamagesMine,
  useFleetMeCompliance,
  useFleetSummary,
  useFleetTasks,
  useFleetVehicles,
  useFleetChargeVehicle,
} from '@/products/fleet/features/use-fleet';
import {
  FLEET_BOOK_PATH,
  FLEET_DRIVERS_PATH,
  FLEET_ODOMETER_PATH,
  FLEET_REPORT_PATH,
  FLEET_REPORTS_PATH,
  FLEET_TASKS_PATH,
  FLEET_VEHICLES_PATH,
  FLEET_VEHICLE_NEW_PATH,
  fleetDamagePath,
  fleetVehiclePath,
} from '@/products/fleet/fleet-paths';

function FleetOwnCharge({ vehicleId, enabled }: { vehicleId: string; enabled: boolean }) {
  const charge = useFleetChargeVehicle(vehicleId, enabled);
  return (
    <span className="flex flex-col items-start gap-1">
      <FleetChargeLevel socPercent={enabled ? (charge.data?.socPercent ?? null) : null} />
      {enabled ? (
        <FleetVehicleChargeLine
          charge={charge.data}
          failedAt={charge.isError ? charge.dataUpdatedAt : null}
        />
      ) : null}
    </span>
  );
}

export function FleetHomePage() {
  usePageTitle('Fuhrparkmanagement');
  const copy = useVoiceCopy();
  const me = useMe();
  const areas = useFleetAreas();
  const catalog = areas.isError ? undefined : areas.data;
  const poolOn = fleetAreaOn(catalog, FleetAreaCode.POOL);
  const mileageOn = fleetAreaOn(catalog, FleetAreaCode.MILEAGE);
  const damageOn = fleetAreaOn(catalog, FleetAreaCode.DAMAGE);
  const complianceOn = fleetAreaOn(catalog, FleetAreaCode.COMPLIANCE);
  const instructionOn = fleetAreaOn(catalog, FleetAreaCode.INSTRUCTION);
  const deadlinesOn = fleetAreaOn(catalog, FleetAreaCode.DEADLINES);
  const telematicsOn = fleetAreaOn(catalog, FleetAreaCode.TELEMATICS);
  const manage = hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const complianceManage =
    hasPermission(me.data, PermissionCode.FLEET_COMPLIANCE_MANAGE) &&
    (complianceOn || instructionOn);
  const pool = hasPermission(me.data, PermissionCode.FLEET_POOL_BOOK) && poolOn;
  const mileage = hasPermission(me.data, PermissionCode.FLEET_MILEAGE_WRITE) && mileageOn;
  const damage = hasPermission(me.data, PermissionCode.FLEET_DAMAGE_CREATE) && damageOn;
  const compliance = useFleetMeCompliance();
  const myVehicles = useFleetVehicles(false, { mine: true });
  const damages = useFleetDamagesMine();
  const tasks = useFleetTasks(!manage);
  const summary = useFleetSummary(manage);

  const ownVehicles = myVehicles.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Fuhrparkmanagement"
        description={
          manage
            ? 'Fahrzeuge, Fristen und Prüfungen.'
            : ownVehicles.length > 0
              ? copy('fleet.home.assigned')
              : copy('fleet.home.none')
        }
      />

      <FleetSolarCard />

      {manage ? (
        <>
          {summary.isPending ? (
            <p className="text-sm text-muted-foreground">Kennzahlen werden geladen …</p>
          ) : null}
          {summary.data ? (
            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
              <SummaryTile
                to={`${FLEET_VEHICLES_PATH}?operationalStatus=in_service`}
                label="Einsatzbereit"
                value={summary.data.inService}
              />
              <SummaryTile
                to={`${FLEET_VEHICLES_PATH}?operationalStatus=out_of_service`}
                label="Außer Betrieb"
                value={summary.data.outOfService}
              />
              <SummaryTile
                to={`${FLEET_TASKS_PATH}?group=${FleetTaskGroup.OVERDUE}`}
                label="Überfällig"
                value={summary.data.overdue}
                valueClassName="text-destructive"
              />
              {damageOn ? (
                <SummaryTile
                  to={FLEET_REPORTS_PATH}
                  label="Offene Schäden"
                  value={summary.data.damageOpen}
                />
              ) : null}
            </div>
          ) : null}
          {summary.data ? (
            <section className="space-y-2">
              <h2 className="text-base font-semibold">Heute</h2>
              {summary.data.today.length === 0 ? (
                <div className={`${fleetPanelClassName} space-y-2`}>
                  <p className="text-sm text-muted-foreground">Noch kein Fahrzeug angelegt.</p>
                  <Button asChild size="sm">
                    <Link to={FLEET_VEHICLE_NEW_PATH}>Fahrzeug anlegen</Link>
                  </Button>
                </div>
              ) : (
                <>
                  <div className={`hidden md:block ${fleetTableFrameClassName}`}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Kennzeichen</TableHead>
                          <TableHead>Fahrer</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Kilometer</TableHead>
                          {deadlinesOn ? <TableHead>Nächste HU</TableHead> : null}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summary.data.today.map((row) => (
                          <TableRow key={row.vehicleId} className="hover:bg-muted/40">
                            <TableCell>
                              <Link
                                to={fleetVehiclePath(row.vehicleId)}
                                className="font-semibold text-primary hover:underline"
                              >
                                {row.licensePlate}
                              </Link>
                            </TableCell>
                            <TableCell>{row.holderName ?? '—'}</TableCell>
                            <TableCell>
                              <FleetStatusBadge status={row.operationalStatus} />
                            </TableCell>
                            <TableCell>
                              {row.currentKm != null
                                ? `${row.currentKm.toLocaleString('de-DE')} km`
                                : '—'}
                            </TableCell>
                            {deadlinesOn ? (
                              <TableCell
                                className={
                                  row.huMissingRegistration
                                    ? 'font-medium text-amber-800 dark:text-amber-300'
                                    : isFleetDateOverdue(row.huDueOn)
                                      ? 'font-medium text-destructive'
                                      : undefined
                                }
                              >
                                {row.huMissingRegistration
                                  ? 'Erstzulassung fehlt'
                                  : row.huDueOn
                                    ? formatFleetDueDate(row.huDueOn)
                                    : '—'}
                              </TableCell>
                            ) : null}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <ul className={`divide-y md:hidden ${fleetTableFrameClassName}`}>
                    {summary.data.today.map((row) => (
                      <li key={row.vehicleId}>
                        <Link
                          to={fleetVehiclePath(row.vehicleId)}
                          className="flex flex-col gap-2 p-3 hover:bg-muted/40"
                        >
                          <span className="text-xl font-semibold text-primary">
                            {row.licensePlate}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {row.holderName ?? 'Kein Fahrer'}
                            {row.currentKm != null
                              ? ` · ${row.currentKm.toLocaleString('de-DE')} km`
                              : ''}
                          </span>
                          <span className="flex flex-wrap items-center gap-2">
                            <FleetStatusBadge status={row.operationalStatus} />
                            {deadlinesOn ? (
                              <span
                                className={
                                  row.huMissingRegistration
                                    ? 'text-sm font-medium text-amber-800 dark:text-amber-300'
                                    : isFleetDateOverdue(row.huDueOn)
                                      ? 'text-sm font-medium text-destructive'
                                      : 'text-sm text-muted-foreground'
                                }
                              >
                                {row.huMissingRegistration
                                  ? 'Erstzulassung fehlt'
                                  : row.huDueOn
                                    ? `HU ${formatFleetDueDate(row.huDueOn)}`
                                    : 'HU —'}
                              </span>
                            ) : null}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          ) : null}
        </>
      ) : null}

      {complianceOn || instructionOn ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Meine nächste Pflicht</CardTitle>
          </CardHeader>
          <CardContent>
            {compliance.isPending ? (
              <p className="text-sm text-muted-foreground">Wird geladen …</p>
            ) : null}
            {compliance.isError ? (
              <p className="text-sm text-destructive">
                {compliance.error instanceof ApiRequestError
                  ? compliance.error.message
                  : 'Status konnte nicht geladen werden.'}
              </p>
            ) : null}
            {compliance.data ? (
              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                {complianceOn ? (
                  <div>
                    <dt className="text-muted-foreground">Führerscheinkontrolle</dt>
                    <dd>
                      {compliance.data.lastLicenseCheck
                        ? `zuletzt ${formatDeDate(compliance.data.lastLicenseCheck.checkedAt)}, fällig ${formatDeDate(compliance.data.nextLicenseCheckDue)}`
                        : 'noch keine Kontrolle erfasst'}
                    </dd>
                  </div>
                ) : null}
                {instructionOn ? (
                  <div>
                    <dt className="text-muted-foreground">UVV-Unterweisung</dt>
                    <dd>
                      {compliance.data.lastInstruction
                        ? `zuletzt ${formatDeDate(compliance.data.lastInstruction.instructedOn)}, fällig ${formatDeDate(compliance.data.nextInstructionDue)}`
                        : 'noch keine Unterweisung erfasst'}
                    </dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {myVehicles.isPending ? (
        <p className="text-sm text-muted-foreground">Fahrzeuge werden geladen …</p>
      ) : null}
      {ownVehicles.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Meine Fahrzeuge</h2>
          <ul className={`${fleetTableFrameClassName} divide-y`}>
            {ownVehicles.map((vehicle) => (
              <li key={vehicle.id}>
                <Link
                  to={fleetVehiclePath(vehicle.id)}
                  className="flex flex-col gap-2 p-3 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="text-xl font-semibold tracking-tight text-primary sm:text-2xl">
                    {vehicle.licensePlate}
                  </span>
                  <span className="flex flex-wrap items-center gap-2 text-sm">
                    <FleetStatusBadge status={vehicle.operationalStatus} />
                    {vehicle.fuelType === 'electric' || vehicle.fuelType === 'hybrid' ? (
                      <FleetOwnCharge vehicleId={vehicle.id} enabled={telematicsOn} />
                    ) : null}
                    {deadlinesOn && vehicle.hu.dueOn ? (
                      <span
                        className={
                          isFleetDateOverdue(vehicle.hu.dueOn)
                            ? 'font-medium text-destructive'
                            : 'text-muted-foreground'
                        }
                      >
                        HU {formatFleetDueDate(vehicle.hu.dueOn)}
                      </span>
                    ) : null}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : !myVehicles.isPending && !manage ? (
        <p className="text-sm text-muted-foreground">{copy('fleet.home.none')}</p>
      ) : null}

      {!manage && tasks.data && tasks.data.items.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Meine Aufgaben</h2>
          <ul className={`${fleetTableFrameClassName} divide-y`}>
            {tasks.data.items.map((item) => (
              <li key={item.id}>
                {item.kind === 'manual' ? (
                  <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                    <span className="font-medium">{item.title}</span>
                    <span className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <span>
                        {item.overdue
                          ? 'Überfällig'
                          : item.dueOn
                            ? formatDeDate(item.dueOn)
                            : 'Offen'}
                      </span>
                      <FleetManualTaskActions task={item} />
                    </span>
                  </div>
                ) : (
                  <Link
                    to={item.subjectHref}
                    className="flex flex-col gap-1 p-3 hover:bg-muted/40 sm:flex-row sm:justify-between"
                  >
                    <span className="font-medium">{item.title}</span>
                    <span className="text-sm text-muted-foreground">
                      {item.overdue
                        ? 'Überfällig'
                        : item.dueOn
                          ? formatDeDate(item.dueOn)
                          : 'Offen'}
                    </span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {damageOn && damages.data && damages.data.items.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Offene Meldungen</h2>
          <ul className={`${fleetTableFrameClassName} divide-y`}>
            {damages.data.items.map((row) => (
              <li key={row.id}>
                <Link
                  to={fleetDamagePath(row.id)}
                  className="flex flex-col gap-1 p-3 hover:bg-muted/40 sm:flex-row sm:justify-between"
                >
                  <span className="font-medium">
                    {row.licensePlate ?? 'Fahrzeug'} · {FLEET_DAMAGE_KIND_LABELS[row.kind]}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {FLEET_DAMAGE_STATUS_LABELS[row.status]}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="hidden flex-wrap gap-2 md:flex">
        {damage ? (
          <Button asChild variant="outline">
            <Link to={FLEET_REPORT_PATH}>Schaden melden</Link>
          </Button>
        ) : null}
        {mileage ? (
          <Button asChild variant="outline">
            <Link to={FLEET_ODOMETER_PATH}>Kilometer melden</Link>
          </Button>
        ) : null}
        {pool ? (
          <Button asChild variant="outline">
            <Link to={FLEET_BOOK_PATH}>Pool buchen</Link>
          </Button>
        ) : null}
        {manage ? (
          <>
            <Button asChild variant="outline">
              <Link to={FLEET_TASKS_PATH}>Aufgaben</Link>
            </Button>
            <Button asChild>
              <Link to={FLEET_VEHICLES_PATH}>Fahrzeuge</Link>
            </Button>
          </>
        ) : null}
        {complianceManage ? (
          <Button asChild variant="outline">
            <Link to={FLEET_DRIVERS_PATH}>Fahrer</Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function SummaryTile({
  to,
  label,
  value,
  valueClassName,
}: {
  to: string;
  label: string;
  value: number;
  valueClassName?: string;
}) {
  return (
    <Link to={to} className="block h-full">
      <Card className="h-full gap-1 py-3 transition-colors hover:bg-muted/40 sm:gap-6 sm:py-6">
        <CardHeader className="gap-0 px-3 sm:px-6">
          <CardTitle className="text-sm leading-snug font-medium sm:text-base">{label}</CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <p className={cn('text-2xl font-semibold sm:text-3xl', valueClassName)}>{value}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
